import React, {useEffect, useRef} from 'react';

const GOOGLE_IDENTITY_SCRIPT_URL = 'https://accounts.google.com/gsi/client';

// One script per page, however many times the login page mounts.
let googleIdentityScript = null;

const loadGoogleIdentityServices = () => {
    if (window.google?.accounts?.id) return Promise.resolve();
    if (!googleIdentityScript) {
        googleIdentityScript = new Promise((resolve, reject) => {
            const script = document.createElement('script');
            script.src = GOOGLE_IDENTITY_SCRIPT_URL;
            script.async = true;
            script.onload = resolve;
            script.onerror = () => {
                // Let a later mount try again (network blocked, ad blocker turned off).
                googleIdentityScript = null;
                reject(new Error('Google Identity Services failed to load'));
            };
            document.head.appendChild(script);
        });
    }
    return googleIdentityScript;
};

/**
 * Google's own "Sign in with Google" button (popup, no One Tap). Hands the ID token
 * to `onCredential`; the api checks it and opens the session.
 */
const GoogleSignInButton = ({onCredential, onUnavailable}) => {
    const buttonRef = useRef(null);
    // Google keeps the callback given at initialisation: it reads the latest handlers here.
    const handlersRef = useRef({onCredential, onUnavailable});
    handlersRef.current = {onCredential, onUnavailable};
    const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;

    useEffect(() => {
        if (!clientId) return;
        let unmounted = false;
        loadGoogleIdentityServices()
            .then(() => {
                if (unmounted || !buttonRef.current) return;
                window.google.accounts.id.initialize({
                    client_id: clientId,
                    callback: ({credential}) => handlersRef.current.onCredential(credential),
                    hd: process.env.REACT_APP_GOOGLE_HOSTED_DOMAIN,
                    ux_mode: 'popup',
                    auto_select: false,
                });
                window.google.accounts.id.renderButton(buttonRef.current, {
                    type: 'standard',
                    theme: 'outline',
                    size: 'large',
                    text: 'signin_with',
                    shape: 'rectangular',
                    locale: 'fr',
                });
            })
            .catch(() => {
                if (!unmounted) handlersRef.current.onUnavailable?.();
            });
        return () => {
            unmounted = true;
        };
    }, [clientId]);

    if (!clientId) return null;
    return <div ref={buttonRef} className="flex justify-center"/>;
};

export default GoogleSignInButton;
